import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Input,
  Space,
  Tag,
  Spin,
  Card,
  Row,
  Col,
  Statistic,
  Divider,
  Avatar,
  Timeline,
  Typography
} from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ClockCircleOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  UserOutlined,
  ShopOutlined,
  EyeOutlined,
  HistoryOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  CloseCircleTwoTone,
  InfoCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { getImageUrl } from "../../component/SafeImage";
import Breadcrumbs from "../../component/Breadcrumbs";
import AdminServ from "../../service/AdminServ";
import UserServ from "../../service/UserServ";
import { formatDate, formatDateTime } from "../../util/dateUtils";
import { getApprovalRowClass } from "../../util/statusHighlight";
import EmptyState from "../../util/EmptyState";
import Notify from "../../util/Notify";
import { FilterBar } from "../../component/ui/FilterBar";
import { PrimaryButton, ResetButton } from "../../component/ui/ActionButtons";
import DataTable from "../../component/ui/DataTable";
import AddUser from "../../component/AddUser";
import { useAdminTable } from "../hooks/useAdminTable";

const SellerRequests = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [userModal, setUserModal] = useState(false);
  const [editUserDetails, setEditUserDetails] = useState(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

  const [action, setAction] = useState(null); // 'approve' or 'reject'

  const fetchRequests = async (params) => {
      const query = {
          limit: params.limit,
          page: params.page
      };
      
      const res = await AdminServ.getAllSellerRequests(query);
      return { 
          data: res?.data || [], 
          total: res?.total || 0,
          stats: res?.stats 
      };
  };

  const {
      data: requests,
      total,
      loading,
      params,
      setParams,
      refresh,
      extraData
  } = useAdminTable(fetchRequests, "admin_seller_requests", {
      page: 1, limit: 10
  });

  const stats = extraData?.stats || { pending: 0, approved: 0, rejected: 0 };

  useEffect(() => {
    if (location.state?.requestId && requests.length > 0) {
      const found = requests.find(r => r._id === location.state.requestId);
      if (found) {
        setSelectedRequest(found);
        setAction("view");
        setIsModalOpen(true);
      }
    }
  }, [location.state, requests]);

  const handleApprove = (record) => {
    setSelectedRequest(record);
    setAction("approve");
    setRemarks("");
    setIsModalOpen(true);
  };

  const handleReject = (record) => {
    setSelectedRequest(record);
    setAction("reject");
    setRemarks("");
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!selectedRequest) return;

    try {
      setLocalLoading(true);

      if (action === "approve") {
        await AdminServ.approveSellerRequest({
          requestId: selectedRequest._id,
          remarks: remarks || "",
        });
        Notify.success("Request approved successfully");
      } else if (action === "reject") {
        if (!remarks.trim()) {
          Notify.error("Please provide a reason for rejection");
          setLocalLoading(false);
          return;
        }

        await AdminServ.rejectSellerRequest({
          requestId: selectedRequest._id,
          remarks: remarks,
        });
        Notify.success("Request rejected successfully");
      }

      setIsModalOpen(false);
      refresh();
    } catch (error) {
      console.error("Error:", error);
      Notify.error(error?.message || "Failed to process request");
    } finally {
      setLocalLoading(false);
    }
  };

  const handleCloseEdit = (shouldRefresh = true) => {
    if (shouldRefresh) refresh();
    setUserModal(false);
    setEditUserDetails(null);
    setIsModalOpen(true); 
  };

  const handleEditSeller = () => {
    if (!selectedRequest?.userId) return;
    setEditUserDetails(selectedRequest.userId);
    setUserModal(true);
    setIsModalOpen(false); 
  };

  const columns = [
    {
      title: "Image",
      key: "image",
      width: 70,
      align: "center",
      render: (_, record) => {
        const img = record.userId?.profilePhoto || record.userId?.profileImage;
        return (
          <Avatar
            size={36}
            src={img ? getImageUrl(img) : null}
            icon={<UserOutlined style={{ color: '#64748b' }} />}
            style={{
              borderRadius: 10,
              border: '1px solid #e2e8f0',
              background: '#f1f5f9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />
        );
      }
    },
    {
      title: "Seller Name",
      dataIndex: ["userId", "firstName"],
      key: "firstName",
      render: (text, record) => (
        <span style={{ fontWeight: 750, color: 'inherit' }}>
          {record.userId?.firstName} {record.userId?.lastName}
        </span>
      ),
    },
    {
      title: "Email",
      dataIndex: ["userId", "email"],
      key: "email",
      render: (email) => <span style={{ opacity: 0.8, fontWeight: 500 }}>{email}</span>
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status) => {
        const s = status?.toString()?.toLowerCase();
        let className = "nz-status-tag ";
        let label = "REJECTED";
        if (s === 'pending') { className += "pending"; label = "PENDING"; }
        else if (s === 'approved') { className += "approved"; label = "APPROVED"; }
        else if (s === 'cancelled') { className += "cancelled"; label = "CANCELLED"; }
        else { className += "rejected"; label = (s || 'REJECTED').toUpperCase(); }

        return (
          <Tag className={className}>
            {label}
          </Tag>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedRequest(record);
            setAction("view");
            setIsModalOpen(true);
          }}
          style={{
            borderRadius: 3,
            fontWeight: 600,
            background: '#ffffffff',
            color: '#004754ff',
            border: '1px solid #e4e4e7',
            boxShadow: '0 0 0 1px #e4e4e7',
            height: 'auto',
            padding: '0px 8px',
            fontSize: 13,
          }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <div className="pcu-padding-24">
      <style>{`
        .nz-status-tag.ant-tag {
          border-radius: 6px !important;
          font-weight: 800 !important;
          font-size: 11px !important;
          padding: 4px 12px !important;
          margin: 0 !important;
          min-width: 90px !important;
          text-align: center !important;
          display: inline-block !important;
          line-height: 1.5 !important;
        }
        .nz-status-tag.pending {
          background: #fffbeb !important;
          border: 1px solid #f59e0b !important;
          color: #d97706 !important;
        }
        .nz-status-tag.approved {
          background: #f0fdf4 !important;
          border: 1px solid #10b981 !important;
          color: #10b981 !important;
        }
        .nz-status-tag.rejected {
          background: #fef2f2 !important;
          border: 1px solid #ef4444 !important;
          color: #ef4444 !important;
        }
        .nz-status-tag.cancelled {
          background: #f1f5f9 !important;
          border: 1px solid #94a3b8 !important;
          color: #64748b !important;
        }
        .seller-row-pending td {
          background: linear-gradient(90deg, #fff8e1 0%, #fffde7 100%) !important;
          border-top: 1.5px solid #fbbf24 !important;
          border-bottom: 1.5px solid #fbbf24 !important;
        }
        .seller-row-pending td:first-child {
          border-left: 4px solid #f59e0b !important;
          border-radius: 0 !important;
        }
        .seller-row-pending td:last-child {
          border-right: 1.5px solid #fbbf24 !important;
        }
        .seller-row-pending:hover td {
          background: linear-gradient(90deg, #fef3c7 0%, #fef9c3 100%) !important;
        }
      `}</style>
      <div className="pcu-margin-bottom-16">
        <Breadcrumbs />
      </div>

      <div className="sd-container">
        <div className="sd-header" style={{ marginBottom: '24px', background: 'transparent', padding: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div className="sd-orders-icon-wrap" style={{ width: '56px', height: '56px', background: '#eff6ff', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #bfdbfe' }}>
              <ShopOutlined style={{ fontSize: '24px', color: '#3b82f6' }} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 800, color: '#1e293b', letterSpacing: '-0.5px' }}>Seller Requests</h1>
              <p style={{ margin: 0, fontSize: '13px', color: '#64748b', fontWeight: 600 }}>{total} requests listed</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={refresh}
              style={{
                height: '42px',
                borderRadius: '10px',
                padding: '0 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: 700,
                color: '#64748b',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
              }}
            >
              Refresh
            </Button>
          </div>
        </div>

        <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
          <Col xs={24} sm={8}>
            <div style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)',
              padding: '16px 20px',
              borderRadius: '12px',
              color: '#fff',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(234, 88, 12, 0.2)'
            }}>
              <div style={{ position: 'absolute', right: -10, bottom: -15, opacity: 0.15 }}>
                <ClockCircleOutlined style={{ fontSize: 80 }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative', zIndex: 2 }}>
                <div style={{ width: 40, height: 40, background: 'rgba(255,255,255,0.25)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ClockCircleOutlined style={{ fontSize: 20, color: '#fff' }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.9)', letterSpacing: '0.5px' }}>PENDING</div>
                  <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.1 }}>{stats.pending}</div>
                </div>
              </div>
            </div>
          </Col>

          <Col xs={24} sm={8}>
            <div style={{
              background: 'linear-gradient(135deg, #2dd4bf 0%, #0d9488 100%)',
              padding: '16px 20px',
              borderRadius: '12px',
              color: '#fff',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(13, 148, 136, 0.2)'
            }}>
              <div style={{ position: 'absolute', right: -10, bottom: -15, opacity: 0.15 }}>
                <CheckCircleOutlined style={{ fontSize: 80 }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative', zIndex: 2 }}>
                <div style={{ width: 40, height: 40, background: 'rgba(255,255,255,0.25)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CheckCircleOutlined style={{ fontSize: 20, color: '#fff' }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.9)', letterSpacing: '0.5px' }}>APPROVED</div>
                  <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.1 }}>{stats.approved}</div>
                </div>
              </div>
            </div>
          </Col>

          <Col xs={24} sm={8}>
            <div style={{
              background: 'linear-gradient(135deg, #fb7185 0%, #e11d48 100%)',
              padding: '16px 20px',
              borderRadius: '12px',
              color: '#fff',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(225, 29, 72, 0.2)'
            }}>
              <div style={{ position: 'absolute', right: -10, bottom: -15, opacity: 0.15 }}>
                <CloseCircleOutlined style={{ fontSize: 80 }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative', zIndex: 2 }}>
                <div style={{ width: 40, height: 40, background: 'rgba(255,255,255,0.25)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CloseCircleOutlined style={{ fontSize: 20, color: '#fff' }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.9)', letterSpacing: '0.5px' }}>REJECTED</div>
                  <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.1 }}>{stats.rejected}</div>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        <div className="ss-table-wrap pcu-margin-top-16">
          <DataTable
            columns={columns}
            data={requests || []}
            rowKey="_id"
            loading={loading}
            size="small"
            bordered={false}
            rowClassName={(record) =>
              record.status?.toLowerCase() === 'pending' ? 'seller-row-pending perm-row' : 'perm-row'
            }
            locale={{
              emptyText: (
                <div className="pcu-padding-y-60">
                  <EmptyState
                    title="No Requests Found"
                    description="There are no seller requests."
                    gradient="orange"
                    icon="inbox"
                  />
                </div>
              ),
            }}
            pagination={{
              current: params.page,
              pageSize: params.limit,
              total: total,
              showTotal: (total) => <span className="pagination-total" style={{ background: '#fff7ed', color: '#c2410c', padding: '4px 12px', borderRadius: '100px', fontWeight: 700, fontSize: '11px' }}>Total {total} items</span>,
              onChange: (p) => {
                  setParams(prev => ({ ...prev, page: p }));
                  const pageContent = document.querySelector('.page-content');
                  if (pageContent) pageContent.scrollTo({ top: 0, behavior: 'smooth' });
                  else window.scrollTo({ top: 0, behavior: 'smooth' });
              },
              onShowSizeChange: (_, size) => setParams(prev => ({ ...prev, limit: size, page: 1 })),
              position: ["bottomRight"],
              size: "small"
            }}
          />
        </div>
      </div>
      {/* Premium Seller Details Modal */}
      <Modal
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedRequest(null);
          setRemarks("");
        }}
        footer={null}
        width={640}
        centered
        className="premium-details-modal"
        styles={{ body: { padding: 0 } }}
        closable={false}
      >
        {selectedRequest && (
          <div className="sd-details-wrap">
            {/* Modal Header */}
            {(() => {
              const u = selectedRequest.userId;
              const displayId = u?.userId || (u?._id ? u._id.substring(0, 12).toUpperCase() : "N/A");
              return (
                <>
                  <div className="sd-details-header" style={{
                    padding: '16px 20px',
                    background: '#ffffff',
                    borderBottom: '1px solid #f1f5f9',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderRadius: '16px 16px 0 0'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <Avatar
                        size={52}
                        src={(selectedRequest.userId?.profilePhoto || selectedRequest.userId?.profileImage) ? getImageUrl(selectedRequest.userId?.profilePhoto || selectedRequest.userId?.profileImage) : null}
                        icon={<UserOutlined style={{ color: '#64748b' }} />}
                        style={{
                          borderRadius: '14px',
                          border: '2px solid #fff',
                          boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                          background: '#f1f5f9',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 900, color: '#0f172a', letterSpacing: '-0.5px', lineHeight: 1.2 }}>
                            {`${selectedRequest.userId?.firstName} ${selectedRequest.userId?.lastName}`}
                          </h2>
                          <Tag className={`nz-status-tag ${selectedRequest.status?.toLowerCase() || 'pending'}`} style={{
                            borderRadius: '100px',
                            fontWeight: 800,
                            fontSize: '10px',
                            padding: '2px 12px',
                            margin: 0,
                            height: '22px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {(selectedRequest.status || 'PENDING').toUpperCase()}
                          </Tag>
                        </div>
                        <p style={{ margin: 0, fontSize: '12px', color: '#64748b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ color: '#94a3b8' }}>ID:</span>
                          <span style={{ color: '#f97316', background: 'rgba(249, 115, 22, 0.08)', padding: '1px 6px', borderRadius: '4px' }}>
                            {displayId.toUpperCase()}
                          </span>
                          <span style={{ color: '#cbd5e1' }}>•</span>
                          <span style={{ letterSpacing: '0.3px' }}>SELLER REQUEST</span>
                        </p>
                      </div>
                    </div>
                    <Button
                      type="text"
                      shape="circle"
                      icon={<CloseOutlined style={{ fontSize: '14px', color: '#94a3b8' }} />}
                      onClick={() => setIsModalOpen(false)}
                      style={{ background: '#f1f5f9', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    />
                  </div>

                  {/* Modal Content */}
                  <div style={{ padding: '20px', background: '#f8fafc' }}>
                    <Row gutter={[12, 12]}>
                      {/* Email Card */}
                      <Col span={24}>
                        <div style={{
                          padding: '12px 16px',
                          background: '#ffffff',
                          border: '1px solid #e2e8f0',
                          borderRadius: '12px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <MailOutlined style={{ color: '#3b82f6', fontSize: '14px' }} />
                          </div>
                          <div>
                            <span style={{ fontSize: '9px', color: '#94a3b8', fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase' }}>Email Address</span>
                            <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>{selectedRequest.userId?.email}</div>
                          </div>
                        </div>
                      </Col>

                      {/* Phone & Status Row */}
                      <Col span={12}>
                        <div style={{
                          padding: '12px 16px',
                          background: '#ffffff',
                          border: '1px solid #e2e8f0',
                          borderRadius: '12px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          height: '100%'
                        }}>
                          <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PhoneOutlined style={{ color: '#22c55e', fontSize: '14px' }} />
                          </div>
                          <div>
                            <span style={{ fontSize: '9px', color: '#94a3b8', fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase' }}>Phone</span>
                            <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>{selectedRequest.userId?.phone || 'N/A'}</div>
                          </div>
                        </div>
                      </Col>
                      <Col span={12}>
                        <div style={{
                          padding: '12px 16px',
                          background: '#ffffff',
                          border: '1px solid #e2e8f0',
                          borderRadius: '12px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          height: '100%'
                        }}>
                          <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <UserOutlined style={{ color: '#f97316', fontSize: '14px' }} />
                          </div>
                          <div>
                            <span style={{ fontSize: '9px', color: '#94a3b8', fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase' }}>Account</span>
                            <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>{selectedRequest.userId?.isActive ? 'ACTIVE' : 'INACTIVE'}</div>
                          </div>
                        </div>
                      </Col>

                      {/* Applied Date & Admin Remarks Row */}
                      <Col span={12}>
                        <div style={{
                          padding: '12px 16px',
                          background: '#ffffff',
                          border: '1px solid #e2e8f0',
                          borderRadius: '12px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          height: '100%'
                        }}>
                          <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CalendarOutlined style={{ color: '#8b5cf6', fontSize: '14px' }} />
                          </div>
                          <div>
                            <span style={{ fontSize: '9px', color: '#94a3b8', fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase' }}>Applied On</span>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: '#1e293b' }}>
                              {new Date(selectedRequest.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col span={12}>
                        <div style={{
                          padding: '12px 16px',
                          background: '#ffffff',
                          border: '1px solid #e2e8f0',
                          borderRadius: '12px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          height: '100%'
                        }}>
                          <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#fff1f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ReloadOutlined style={{ color: '#f43f5e', fontSize: '14px' }} />
                          </div>
                          <div>
                            <span style={{ fontSize: '9px', color: '#94a3b8', fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase' }}>Remarks</span>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: '#1e293b', fontStyle: 'italic', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {selectedRequest.remarks || 'None'}
                            </div>
                          </div>
                        </div>
                      </Col>

                      {/* Remarks Input for Action */}
                      {(action === "reject" || action === "approve") && (
                        <Col span={24}>
                          <div style={{
                            marginTop: "8px",
                            padding: '16px',
                            background: action === 'approve' ? '#f0fdf4' : '#fef2f2',
                            borderRadius: '14px',
                            border: `1px solid ${action === 'approve' ? '#bbf7d0' : '#fecaca'}`,
                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)'
                          }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 900, fontSize: '10px', color: action === 'approve' ? '#15803d' : '#b91c1c', letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                              {action === "reject" ? "REASON FOR REJECTION (REQUIRED)" : "APPROVAL REMARKS (OPTIONAL)"}
                            </label>
                            <Input.TextArea
                              placeholder={action === "reject" ? "Explain why this request is being rejected..." : "Any internal notes for this approval..."}
                              value={remarks}
                              onChange={(e) => setRemarks(e.target.value)}
                              rows={2}
                              style={{
                                borderRadius: '8px',
                                border: `1px solid ${action === 'approve' ? '#86efac' : '#fca5a5'}`,
                                padding: '12px',
                                fontSize: '13px',
                                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.03)'
                              }}
                            />
                          </div>
                        </Col>
                      )}
                    </Row>
                  </div>
                </>
              );
            })()}

            {/* Modal Footer */}
            <div style={{
              padding: '8px 16px',
              background: '#ffffff',
              borderTop: '1px solid #f1f5f9',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderRadius: '0 0 16px 16px'
            }}>
              <div style={{ display: 'flex', gap: '6px' }}>
                <Button
                  icon={<HistoryOutlined style={{ fontSize: '11px' }} />}
                  style={{ borderRadius: '6px', fontWeight: 700, height: '28px', border: '1.2px solid #e2e8f0', color: '#64748b', fontSize: '11px', padding: '0 10px' }}
                  onClick={() => setIsHistoryModalOpen(true)}
                >
                  History
                </Button>
                <Button
                  icon={<EditOutlined style={{ fontSize: '11px' }} />}
                  style={{ borderRadius: '6px', fontWeight: 700, height: '28px', border: '1.2px solid #3b82f6', color: '#3b82f6', fontSize: '11px', padding: '0 10px' }}
                  onClick={handleEditSeller}
                >
                  Edit
                </Button>

                {selectedRequest.status === 'pending' && action === 'view' ? (
                  <>
                    <Button
                      type="primary"
                      style={{ background: '#10B981', borderColor: '#10B981', fontWeight: 800, borderRadius: '6px', height: '28px', padding: '0 12px', fontSize: '11px' }}
                      onClick={() => setAction('approve')}
                    >
                      Approve Request
                    </Button>
                    <Button
                      danger
                      style={{ fontWeight: 800, borderRadius: '6px', height: '28px', padding: '0 12px', border: '1.2px solid #ef4444', background: 'transparent', color: '#ef4444', fontSize: '11px' }}
                      onClick={() => setAction('reject')}
                    >
                      Reject Request
                    </Button>
                  </>
                ) : (
                  action !== 'view' && (
                    <Button
                      type="primary"
                      style={{
                        background: action === 'approve' ? '#10B981' : '#EF4444',
                        borderColor: action === 'approve' ? '#10B981' : '#EF4444',
                        fontWeight: 800,
                        borderRadius: '6px',
                        height: '28px',
                        padding: '0 12px',
                        fontSize: '11px'
                      }}
                      onClick={handleSubmit}
                    >
                      {action === 'approve' ? 'Confirm' : 'Confirm'}
                    </Button>
                  )
                )}

                {action !== 'view' && (
                  <Button
                    onClick={() => {
                      setAction('view');
                      setRemarks("");
                    }}
                    style={{ fontWeight: 700, borderRadius: '6px', height: '28px', border: '1.2px solid #e2e8f0', fontSize: '11px', padding: '0 10px' }}
                  >
                    Back
                  </Button>
                )}
              </div>

              <Button
                onClick={() => setIsModalOpen(false)}
                style={{ borderRadius: '6px', fontWeight: 800, background: '#f8fafc', border: 'none', height: '28px', padding: '0 16px', color: '#1e293b', fontSize: '11px' }}
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* User Edit Modal */}
      {userModal && (
        <AddUser
          open={userModal}
          userDetails={editUserDetails}
          isEditing={true}
          refreshUsers={handleCloseEdit}
        />
      )}

      {/* Dynamic History Modal - Premium Style */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px' }}>
            <div style={{ width: 40, height: 40, background: '#eff6ff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <HistoryOutlined style={{ color: '#3b82f6', fontSize: '20px' }} />
            </div>
            <div>
              <span style={{ fontWeight: 900, fontSize: '18px', color: '#1e293b', display: 'block', lineHeight: 1.2 }}>Request Audit Trail</span>
              <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>Detailed lifecycle tracking</span>
            </div>
          </div>
        }
        open={isHistoryModalOpen}
        onCancel={() => setIsHistoryModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsHistoryModalOpen(false)} style={{ borderRadius: '8px', fontWeight: 700, height: '40px', padding: '0 24px', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b' }}>
            Done
          </Button>
        ]}
        width={550}
        centered
        closable={false}
        styles={{ content: { borderRadius: '20px', padding: 0, overflow: 'hidden' }, body: { padding: 0 } }}
      >
        <div style={{ padding: '24px', background: '#fff', maxHeight: '65vh', overflowY: 'auto' }}>
          {selectedRequest?.statusHistory && selectedRequest.statusHistory.length > 0 ? (
            <Timeline
              mode="left"
              className="premium-timeline-vertical"
              items={selectedRequest.statusHistory.slice().reverse().map((item, index) => {
                const isFirst = index === 0;
                const status = item.status?.toLowerCase();

                const getStatusConfig = (s) => {
                  switch (s) {
                    case 'approved': return { color: '#10b981', icon: <CheckCircleOutlined />, bg: '#f0fdf4' };
                    case 'rejected': return { color: '#ef4444', icon: <CloseCircleOutlined />, bg: '#fef2f2' };
                    case 'cancelled': return { color: '#64748b', icon: <CloseOutlined />, bg: '#f8fafc' };
                    case 'pending': return { color: '#f59e0b', icon: <ClockCircleOutlined />, bg: '#fffbeb' };
                    default: return { color: '#3b82f6', icon: <InfoCircleOutlined />, bg: '#eff6ff' };
                  }
                };

                const config = getStatusConfig(status);

                return {
                  dot: (
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '10px',
                      background: config.bg,
                      color: config.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: `1px solid ${config.color}33`,
                      boxShadow: isFirst ? `0 0 0 4px ${config.color}15` : 'none',
                      zIndex: 2,
                      fontSize: '16px'
                    }}>
                      {config.icon}
                    </div>
                  ),
                  children: (
                    <div style={{
                      marginLeft: '12px',
                      padding: '16px',
                      background: isFirst ? '#f8fafc' : '#fff',
                      border: `1px solid ${isFirst ? '#e2e8f0' : '#f1f5f9'}`,
                      borderRadius: '16px',
                      marginBottom: '24px',
                      boxShadow: isFirst ? '0 4px 12px rgba(0,0,0,0.03)' : 'none',
                      position: 'relative'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div>
                          <Tag style={{
                            background: config.bg,
                            color: config.color,
                            border: 'none',
                            borderRadius: '6px',
                            fontWeight: 800,
                            fontSize: '10px',
                            margin: 0,
                            padding: '0 8px',
                            textTransform: 'uppercase'
                          }}>
                            {status}
                          </Tag>
                          {isFirst && <span style={{ marginLeft: '8px', fontSize: '9px', fontWeight: 800, color: '#3b82f6', background: '#eff6ff', padding: '2px 6px', borderRadius: '4px' }}>LATEST</span>}
                        </div>
                        <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>
                          {formatDateTime(item.timestamp)}
                        </span>
                      </div>

                      <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', lineHeight: 1.4, marginBottom: '12px' }}>
                        {item.remarks || (status === 'pending' ? 'Initial application submitted for review' : 'Status transition logged')}
                      </div>

                      {item.processedBy && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          paddingTop: '12px',
                          borderTop: '1px dashed #e2e8f0',
                          marginTop: '4px'
                        }}>
                          <Avatar
                            size={20}
                            src={(item.processedBy.profilePhoto || item.processedBy.profileImage) ? getImageUrl(item.processedBy.profilePhoto || item.processedBy.profileImage) : null}
                            icon={<UserOutlined style={{ color: '#fff' }} />}
                            style={{ background: '#64748b', fontSize: '10px' }}
                          />
                          <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>
                            By <span style={{ color: '#1e293b', fontWeight: 750 }}>
                              {typeof item.processedBy === 'object' && item.processedBy.firstName
                                ? `${item.processedBy.firstName} ${item.processedBy.lastName || ''}`
                                : (typeof item.processedBy === 'string' ? item.processedBy : 'System / Admin')}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                  )
                };
              })}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <EmptyState
                title="No History Logs"
                description="This request hasn't undergone any status changes yet."
                showAction={false}
                icon="clock"
              />
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default SellerRequests;

